import { Injectable } from '@angular/core';
import { RouterStateSnapshot } from '@angular/router';
import {
  ActivatedRouterStateSnapshot,
  CmsActivatedRouteSnapshot,
  HOME_PAGE_CONTEXT,
  PageType,
  RoutingConfigService,
  SMART_EDIT_CONTEXT
} from '@spartacus/core';
import { RouterStateSerializer } from '@ngrx/router-store';
import { CustomPageContext } from '../../models';
import { getPdpType } from '../../utils';

@Injectable({
  providedIn: 'root'
})
export class CustomRouterStateSerializer implements RouterStateSerializer<ActivatedRouterStateSnapshot> {
  constructor(private routingConfig: RoutingConfigService) {}

  serialize(routerState: RouterStateSnapshot): ActivatedRouterStateSnapshot {
    let state: CmsActivatedRouteSnapshot = routerState.root as CmsActivatedRouteSnapshot;
    let cmsRequired = false;
    let context: CustomPageContext;
    let semanticRoute: string;
    let urlString = '';

    while (state.firstChild) {
      state = state.firstChild as CmsActivatedRouteSnapshot;
      urlString += '/' + state.url.map((urlSegment) => urlSegment.path).join('/');

      // we use semantic route information embedded from any parent route
      if (state.data?.cxRoute) {
        semanticRoute = state.data?.cxRoute;
      }
      // we use context information embedded in Cms driven routes from any parent route
      if (state.data && state.data.cxCmsRouteContext) {
        context = state.data.cxCmsRouteContext;
      }

      // we assume, that any route that has CmsPageGuard or it's child
      // is cmsRequired
      if (
        !cmsRequired &&
        (context ||
          (state.routeConfig &&
            state.routeConfig.canActivate &&
            state.routeConfig.canActivate.find((x) => x && x.guardName === 'CmsPageGuard')))
      ) {
        cmsRequired = true;
      }
    }

    // If `semanticRoute` couldn't be already recognized using `data.cxRoute` property
    // let's lookup the routing configuration to find the semantic route that has exactly the same configured path as the current URL.
    // This will work only for simple URLs without any dynamic routing parameters.
    semanticRoute = semanticRoute || this.lookupSemanticRoute(urlString);
    const { params } = state;
    // we give smartedit preview page a PageContext
    if (state.url.length > 0 && state.url[0].path === 'cx-preview') {
      context = {
        id: SMART_EDIT_CONTEXT,
        type: PageType.CONTENT_PAGE
      };
    } else {
      if (params['productCode']) {
        const pdpType = getPdpType(routerState.root.children[0].params['param0']);
        context = { id: params['productCode'], type: PageType.PRODUCT_PAGE, customPageType: pdpType };
      } else if (this.isCategoryServicePage(state, params)) {
        context = { id: params['categoryCode'], type: PageType.CATEGORY_PAGE };
      } else if (params['brandCode']) {
        context = { id: params['brandCode'], type: PageType.CATEGORY_PAGE };
      } else if (params['recipeCategoryCode']) {
        context = { id: params['recipeCategoryCode'], type: PageType.CONTENT_PAGE, customPageType: PageType.RECIPE_CATEGORY_PAGE as PageType };
      } else if (state.data.pageLabel !== undefined) {
        context = { id: state.data.pageLabel, type: PageType.CONTENT_PAGE };
      } else if (!context) {
        if (state.url.length > 0) {
          const pageLabel = '/' + state.url.map((urlSegment) => urlSegment.path).join('/');
          context = {
            id: pageLabel,
            type: PageType.CONTENT_PAGE
          };
        } else {
          context = {
            // We like URLs to be driven by the backend, the CMS actually returns the homepage
            // if no page label is given. Our logic however requires an id. undefined doesn't work.
            id: HOME_PAGE_CONTEXT,

            // We currently need to support a hardcoded page type, since the internal store uses the page
            // type to store the content.
            type: PageType.CONTENT_PAGE
          };
        }
      }
    }

    return {
      url: routerState.url,
      queryParams: routerState.root.queryParams,
      params,
      context,
      cmsRequired,
      semanticRoute
    };
  }

  isCategoryServicePage(state, params): boolean {
    const pathIndex = params['brand'] ? 1 : 0;
    const currentPath = state.url?.[pathIndex]?.path;

    return params['categoryCode'] &&
      currentPath !== 'instructions-for-use' &&
      currentPath !== 'faq' &&
      currentPath !== 'my-products';
  }

  /**
   * Returns the semantic route name for given page label.
   *
   * *NOTE*: It works only for simple static urls that are equal to the page label
   * of cms-driven content page. For example: `/my-account/address-book`.
   *
   * It doesn't work for URLs with dynamic parameters. But such case can be handled
   * by reading the defined `data.cxRoute` from the Angular Routes.
   *
   * @param path path to be found in the routing config
   */
  private lookupSemanticRoute(path: string): string {
    // Page label is assumed to start with `/`, but Spartacus configured paths
    // don't start with slash. So we remove the leading slash:
    return this.routingConfig.getRouteName(path.substr(1));
  }
}
